/**
 * Job Health Monitor Hook
 * 
 * Periodically checks running jobs for anomalies and emits notifications.
 * Checks: out-of-range ratio, failure rate, stall detection, unexpected URL counts.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';

export interface JobHealthAlert {
  id: string;
  type: 'warning' | 'error' | 'info';
  category: 'out-of-range' | 'stalled' | 'high-failure' | 'long-running';
  message: string;
  jobId: string;
  jobName: string;
  runId: string;
  timestamp: Date;
  actions?: Array<{ label: string; action: 'pause' | 'cancel' | 'view' }>;
}

const STALL_THRESHOLD_MS = 30 * 60 * 1000; // 30 minutes
const LONG_RUNNING_THRESHOLD_MS = 2 * 60 * 60 * 1000; // 2 hours
const FAILURE_RATE_THRESHOLD = 0.2; // 20%
const CHECK_INTERVAL_MS = 60 * 1000; // Check every 60 seconds

export const useJobHealthMonitor = () => {
  const [alerts, setAlerts] = useState<JobHealthAlert[]>([]);
  const seenAlertIds = useRef(new Set<string>());

  const addAlert = useCallback((alert: Omit<JobHealthAlert, 'id' | 'timestamp'>) => {
    const id = `${alert.category}-${alert.runId}`;
    if (seenAlertIds.current.has(id)) return;
    seenAlertIds.current.add(id);

    setAlerts(prev => [
      { ...alert, id, timestamp: new Date() },
      ...prev.slice(0, 19), // Keep last 20
    ]);
  }, []);

  const dismissAlert = useCallback((alertId: string) => {
    setAlerts(prev => prev.filter(a => a.id !== alertId));
  }, []);

  const clearAlerts = useCallback(() => {
    setAlerts([]);
    seenAlertIds.current.clear();
  }, []);

  const checkRunningJobs = useCallback(async () => {
    try {
      // Get all running job runs with their job names
      const { data: runs } = await (supabase as any)
        .from('job_runs')
        .select(`
          id, job_id, status, phase, started_at, last_activity_at,
          urls_discovered, urls_scraped, records_scraped,
          scrape_jobs!job_runs_job_id_fkey(name)
        `)
        .eq('status', 'running');

      if (!runs || runs.length === 0) return;

      for (const run of runs) {
        const jobName = run.scrape_jobs?.name || 'Unknown Job';
        const now = Date.now();

        // Check for stalled jobs (no activity in 30 minutes)
        if (run.last_activity_at) {
          const lastActivity = new Date(run.last_activity_at).getTime();
          if (now - lastActivity > STALL_THRESHOLD_MS) {
            addAlert({
              type: 'warning',
              category: 'stalled',
              message: `"${jobName}" appears stalled - no activity for ${Math.round((now - lastActivity) / 60000)} minutes`,
              jobId: run.job_id,
              jobName,
              runId: run.id,
              actions: [
                { label: 'View Details', action: 'view' },
                { label: 'Pause', action: 'pause' },
                { label: 'Cancel', action: 'cancel' },
              ],
            });
          }
        }

        // Check for long-running jobs
        const startedAt = new Date(run.started_at).getTime();
        if (now - startedAt > LONG_RUNNING_THRESHOLD_MS) {
          addAlert({
            type: 'info',
            category: 'long-running',
            message: `"${jobName}" has been running for ${Math.round((now - startedAt) / 3600000)} hours`,
            jobId: run.job_id,
            jobName,
            runId: run.id,
            actions: [
              { label: 'View Details', action: 'view' },
              { label: 'Pause', action: 'pause' },
            ],
          });
        }

        // Check failure rate (only if we have enough data)
        if (run.urls_scraped && run.urls_scraped >= 10) {
          const { data: queueStats } = await (supabase as any)
            .from('scrape_queue')
            .select('status')
            .eq('job_run_id', run.id);

          if (queueStats) {
            const failed = queueStats.filter((q: { status: string }) => q.status === 'failed').length;
            const completed = queueStats.filter((q: { status: string }) => q.status === 'completed').length;
            const total = failed + completed;

            if (total >= 10 && failed / total > FAILURE_RATE_THRESHOLD) {
              addAlert({
                type: 'warning',
                category: 'high-failure',
                message: `"${jobName}" has a ${Math.round((failed / total) * 100)}% failure rate (${failed}/${total})`,
                jobId: run.job_id,
                jobName,
                runId: run.id,
                actions: [
                  { label: 'View Details', action: 'view' },
                  { label: 'Pause', action: 'pause' },
                ],
              });
            }

            // Check out-of-range rate
            const { data: oorItems } = await (supabase as any)
              .from('scrape_queue')
              .select('is_out_of_range')
              .eq('job_run_id', run.id)
              .eq('status', 'completed');

            if (oorItems && oorItems.length >= 10) {
              const outOfRange = oorItems.filter((q: { is_out_of_range: boolean }) => q.is_out_of_range).length;
              const oorRate = outOfRange / oorItems.length;

              if (oorRate > 0.3) {
                addAlert({
                  type: 'warning',
                  category: 'out-of-range',
                  message: `"${jobName}" has ${Math.round(oorRate * 100)}% out-of-range results (${outOfRange}/${oorItems.length})`,
                  jobId: run.job_id,
                  jobName,
                  runId: run.id,
                  actions: [
                    { label: 'View Details', action: 'view' },
                    { label: 'Pause', action: 'pause' },
                    { label: 'Cancel', action: 'cancel' },
                  ],
                });
              }
            }
          }
        }
      }
    } catch (error) {
      console.error('Job health check failed:', error);
    }
  }, [addAlert]);

  useEffect(() => {
    // Initial check
    checkRunningJobs();

    // Periodic checks
    const interval = setInterval(checkRunningJobs, CHECK_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [checkRunningJobs]);

  return {
    alerts,
    dismissAlert,
    clearAlerts,
    checkNow: checkRunningJobs,
  };
};
